import React, { useState, useRef, useEffect } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useSelector } from 'react-redux';
import * as Mui from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import { obterImagemAssinatura } from '~/components/Assinatura/assinatura.helper';
import ModalCompartilharDocumento from '../ModalCompartilharDocumento';
import DialogHeader from '../../components/Impressao/DialogHeader';
import PrintDialogActions from '../../components/Impressao/PrintDialogActions';
import UserInfoSection from '../../components/Impressao/UserInfoSection';
import OrcamentoIdentificationSection from '../../components/Impressao/OrcamentoIdentificationSection';
import ItensTable from '../../components/Impressao/ItensTable';
import ComplementaryInfo from '../../components/Impressao/ComplementaryInfo';
import TotalizersSection from '../../components/Impressao/TotalizersSection';
import { selectorParametroAsInteger, selectorRotina } from '~/store/modules/auth/auth-selectors';
import AssinaturaSection from '../../components/Impressao/AssinaturaSection';
import { TIPO_DOCUMENTO, ASSINATURA_TIPO_DOCUMENTO, MODO_IMPRESSAO } from '~/constants';
import assinaturaService from '~/store/modules/assinatura/assinatura.service';
import { useTheme } from '@material-ui/core/styles';

import './index.css'
import { DISPLAY_CUSTOM_STYLES, PRINT_CUSTOM_STYLES_A4, PRINT_CUSTOM_STYLES_A5 } from './consts';

const ModalImprimirOrcamento = ({ open, separaPorAmbiente, orcamento, onClose }) => {

  const theme = useTheme();

  orcamento.itens = orcamento.itens.filter(item => !item.itemCancelado);
  const empresa = useSelector(state => state?.auth?.empresa ?? 0);
  const user = useSelector(state => state?.auth?.usuario ?? 0);
  const componentRef = useRef(null);
  const [modalEnviarOrcamento, setModalEnviarOrcamento] = useState(false);
  const [habilitaImagem, setHabilitaImagem] = useState(() => {
    const saved = localStorage.getItem('image_toggle_on_print');
    return saved ? JSON.parse(saved) : false;
  });
  let modoImpressao = useSelector(selectorParametroAsInteger('PZ013')) ?? MODO_IMPRESSAO.A4;
  const printStyles = modoImpressao === MODO_IMPRESSAO.A4 ? PRINT_CUSTOM_STYLES_A4 : PRINT_CUSTOM_STYLES_A5;
  const displayStyles = DISPLAY_CUSTOM_STYLES;
  const permissaoUsuarioVerAssinatura = useSelector(selectorRotina('A040ASS')); // Veja as assinaturas dos documentos

  const [assinatura, setAssinatura] = useState(orcamento?.intAssinatura ?? null);
  const [loadingAssinatura, setLoadingAssinatura] = useState(false);

  useEffect(() => {
    if (!permissaoUsuarioVerAssinatura || !orcamento?.id) return;

    const loadAssinatura = async () => {
      try {
        setLoadingAssinatura(true);
        const response = await assinaturaService.getAssinaturaByDocumento({
          tipoDocumento: ASSINATURA_TIPO_DOCUMENTO.ORCAMENTO,
          documentoId: orcamento.id,
        });
        setAssinatura(response.data ?? null);
      } catch (error) {
        console.error('Erro ao buscar assinatura do orçamento:', error);
      } finally {
        setLoadingAssinatura(false);
      }
    };

    loadAssinatura();
  }, [permissaoUsuarioVerAssinatura, orcamento?.id]);

  const imagemAssinatura = React.useMemo(() => {
      return obterImagemAssinatura({
          dados: assinatura?.assinatura,
          tipo: "image/png",
          qualidade: 1,
          corFundo: null,
          corCaneta: theme.palette.text.primary,
          margem: 10,
          exportScale: 2,
          espessuraMultiplicador: 1,
          larguraMaxima: 260,
          alturaMaxima: 80,
      });
  }, [
      assinatura?.assinatura,
      theme.palette.text.primary
  ]);

  const imagemAssinaturaPrint = React.useMemo(() => {
      return obterImagemAssinatura({
          dados: assinatura?.assinatura,
          tipo: "image/png",
          qualidade: 1,
          corFundo: "#FFFFFF",
          corCaneta: "#000000",
          margem: 10,
          exportScale: 2,
          espessuraMultiplicador: 1,
          larguraMaxima: 260,
          alturaMaxima: 80,
      });
  }, [
      assinatura?.assinatura
  ]);

  useEffect(() => {
    localStorage.setItem('image_toggle_on_print', JSON.stringify(habilitaImagem));
  }, [habilitaImagem]);

  const handleShare = () => setModalEnviarOrcamento(true);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Orçamento-${empresa.razaoSocial}-${orcamento?.serie}-${orcamento?.numero}`,
    pageStyle: `
      .assinatura-print {
        display: none;
      }

      .assinatura-linha {
        border-top: 1px solid currentColor;
      }

      @media print {

        #printable-content {
          --spacing-size: ${printStyles.spacingSize}px !important;
          --half-spacing-size: ${printStyles.halfSpacingSize}px !important;
          --horizontal-item-grid-spacing: ${printStyles.horizontalItemGridSpacing}px !important;
          --vertical-item-grid-spacing: ${printStyles.verticalItemGridSpacing}px !important;
          --regular-text-size: ${printStyles.regularTextSize}px !important;
          --section-title-text-size: ${printStyles.sectionTitleTextSize}px !important;
          --main-title-text-size: ${printStyles.mainTitleTextSize}px !important;
          --url-logo-size: ${printStyles.urlLogoSize}px !important;
          --item-img-size: ${printStyles.itemImgSize}px !important;
          --small-text-size: ${printStyles.smallTextSize}px !important;
        }

        body * {
          visibility: hidden;
        }

        #printable-content, #printable-content * {
          visibility: visible;
        }

        #printable-content {
          width: 100%;
          box-sizing: border-box;
          zoom: 1;
          overflow: visible !important;
          max-height: none !important;
        }

        .scrollable {
          overflow: visible !important;
        }

        .no-break {
          page-break-inside: avoid;
          break-inside: avoid;
        }

        .assinatura-tela {
          display: none !important;
        }

        .assinatura-print {
          display: block !important;
        }

        .assinatura-linha {
          border-top: 1px solid #000 !important;
          color: #000 !important;
        }

        .assinatura-section {
          color: #000 !important;
        }
      }
    `,
  });

  return (
    <>
      <Mui.Dialog
        open={open}
        onKeyDown={key => {
          if (key.code === 'Escape') {
            onClose();
          }
        }}
        PaperComponent={Paper}
        maxWidth={'lg'}
        fullWidth={true}
      >

        <DialogHeader title="Impressão de Orçamento" onClose={onClose} />

        <Mui.DialogContent dividers style={{ padding: `${displayStyles.halfSpacingSize}px` }}>
          {loadingAssinatura ? (
            <Mui.Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <Mui.CircularProgress variant="indeterminate" color="primary" />
            </Mui.Box>
          ) : (
            <div id="printable-content" ref={componentRef} style={{
              '--spacing-size': `${displayStyles.spacingSize}px`,
              '--half-spacing-size': `${displayStyles.halfSpacingSize}px`,
              '--horizontal-item-grid-spacing': `${displayStyles.horizontalItemGridSpacing}px`,
              '--vertical-item-grid-spacing': `${displayStyles.verticalItemGridSpacing}px`,
              '--regular-text-size': `${displayStyles.regularTextSize}px`,
              '--section-title-text-size': `${displayStyles.sectionTitleTextSize}px`,
              '--main-title-text-size': `${displayStyles.mainTitleTextSize}px`,
              '--url-logo-size': `${displayStyles.urlLogoSize}px`,
              '--item-img-size': `${displayStyles.itemImgSize}px`,
              '--small-text-size': `${displayStyles.smallTextSize}px`,
            }}>
              {
                <>
                  <UserInfoSection user={user} empresa={empresa} documento={orcamento} title="Orçamento" tipoDocumento={TIPO_DOCUMENTO.ORCAMENTO} modoImpressao={modoImpressao} />
                  <OrcamentoIdentificationSection documento={orcamento} title="Orçamento" tipoDocumento={TIPO_DOCUMENTO.ORCAMENTO} modoImpressao={modoImpressao} />
                  <ItensTable itens={orcamento.itens} separaPorAmbiente={separaPorAmbiente} habilitaImagem={habilitaImagem} modoImpressao={modoImpressao} />
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
                    <div style={{ flex: '2 1 auto', display: 'flex', flexDirection: modoImpressao === MODO_IMPRESSAO.A4 ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'stretch', maxWidth: '60%', border: '1px solid black', borderRight: 'none' }}>
                      <ComplementaryInfo info={orcamento.informacaoComplementar} modoImpressao={modoImpressao} />
                      <AssinaturaSection permissaoUsuarioVerAssinatura={permissaoUsuarioVerAssinatura ?? false} dadosAssinatura={assinatura?.assinatura ?? null} imagemAssinatura={imagemAssinatura} imagemAssinaturaPrint={imagemAssinaturaPrint} data={assinatura?.logAtualizacao ?? null} modoImpressao={modoImpressao} />
                    </div>
                    <div className='scrollable' style={{ overflowX: 'auto', flex: '1 1 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', border: '1px solid black' }}>
                      <TotalizersSection documento={orcamento} modoImpressao={modoImpressao} />
                    </div>
                  </div>
                </>
              }
            </div>
          )}
        </Mui.DialogContent>

        {!loadingAssinatura && (
          <PrintDialogActions
            onPrint={handlePrint}
            onClose={onClose}
            onShare={handleShare}
            habilitaImagem={habilitaImagem}
            onToggleImagem={setHabilitaImagem}
          />
        )}

        {modalEnviarOrcamento && (
          <ModalCompartilharDocumento
            open={modalEnviarOrcamento}
            onClose={() => setModalEnviarOrcamento(false)}
            onCloseImpressao={onClose}
            emailCliente={orcamento?.intCliente?.email}
            telefoneCliente={orcamento?.intCliente?.celular}
            documento={orcamento}
            tipoDocumento={TIPO_DOCUMENTO.ORCAMENTO}
            printStyles={printStyles}
            modoImpressao={modoImpressao}
          />
        )}
      </Mui.Dialog>
    </>
  );
};

export default ModalImprimirOrcamento;
